import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, map, mergeMap, switchMap } from "rxjs/operators";

import { DataRightsAdminService } from "../../../services/api/data-rights-admin.service";
import { DataRightsGroupService } from "../../../services/api/data-rights-group.service";
import { DataRightsAdminActions } from "../../actions/data-rights-admin.actions";
import { FailState } from "../../models/fail-state";

@Injectable()
export class DataRightsAdminEffects {
	search = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.group.search),
			switchMap(({ criteria }) =>
				this.service.search(criteria).pipe(
					map((result) => DataRightsAdminActions.group.searchSuccess({ result })),
					catchError((error) =>
						of(DataRightsAdminActions.group.searchFail({ error: new FailState(error) }))
					)
				)
			)
		)
	);

	getById = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.group.getById),
			switchMap(({ id }) =>
				this.service.getById(id).pipe(
					map((group) => DataRightsAdminActions.group.getByIdSuccess({ group })),
					catchError((error) =>
						of(DataRightsAdminActions.group.getByIdFail({ error: new FailState(error) }))
					)
				)
			)
		)
	);

	create = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.group.create),
			mergeMap(({ request }) =>
				this.service.create(request).pipe(
					map((group) => DataRightsAdminActions.group.createSuccess({ group })),
					catchError((error) =>
						of(DataRightsAdminActions.group.createFail({ error: new FailState(error) }))
					)
				)
			)
		)
	);

	update = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.group.update),
			mergeMap(({ id, request }) =>
				this.service.update(id, request).pipe(
					map((group) => DataRightsAdminActions.group.updateSuccess({ group })),
					catchError((error) =>
						of(DataRightsAdminActions.group.updateFail({ error: new FailState(error) }))
					)
				)
			)
		)
	);

	delete = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.group.delete),
			mergeMap(({ id }) =>
				this.service.delete(id).pipe(
					map(() => DataRightsAdminActions.group.deleteSuccess({ id })),
					catchError((error) =>
						of(DataRightsAdminActions.group.deleteFail({ error: new FailState(error) }))
					)
				)
			)
		)
	);

	promoteDefault = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.group.promoteDefault),
			mergeMap(({ id }) =>
				this.service.promoteDefault(id).pipe(
					map((group) =>
						DataRightsAdminActions.group.promoteDefaultSuccess({ group })
					),
					catchError((error) =>
						of(
							DataRightsAdminActions.group.promoteDefaultFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	setMembership = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.group.setMembership),
			mergeMap(({ id, members }) =>
				this.service.setMembership(id, members).pipe(
					map((group) =>
						DataRightsAdminActions.group.setMembershipSuccess({ group })
					),
					catchError((error) =>
						of(
							DataRightsAdminActions.group.setMembershipFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	availableRights = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.availableRights.get),
			switchMap(() =>
				this.service.getAvailableDataRightsForCurrentConsumer().pipe(
					map((rights) =>
						DataRightsAdminActions.availableRights.getSuccess({ rights })
					),
					catchError((error) =>
						of(
							DataRightsAdminActions.availableRights.getFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	availableGroups = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.availableGroups.get),
			switchMap(() =>
				this.service.getAvailableDataRightsGroupsForCurrentConsumer().pipe(
					map((groups) =>
						DataRightsAdminActions.availableGroups.getSuccess({ groups })
					),
					catchError((error) =>
						of(
							DataRightsAdminActions.availableGroups.getFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	consumerGet = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.consumer.get),
			switchMap(({ consumerId }) =>
				this.service.getConsumerGroups(consumerId).pipe(
					map((groups) =>
						DataRightsAdminActions.consumer.getSuccess({ consumerId, groups })
					),
					catchError((error) =>
						of(
							DataRightsAdminActions.consumer.getFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	consumerSet = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.consumer.set),
			mergeMap(({ consumerId, dataRightsGroupIds }) =>
				this.service.setConsumerGroups(consumerId, dataRightsGroupIds).pipe(
					map((groups) =>
						DataRightsAdminActions.consumer.setSuccess({ consumerId, groups })
					),
					catchError((error) =>
						of(
							DataRightsAdminActions.consumer.setFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	listRights = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.rights.list),
			switchMap(({ criteria }) =>
				this.rightsService.search(criteria ?? {}).pipe(
					map((result) => DataRightsAdminActions.rights.listSuccess({ result })),
					catchError((error) =>
						of(
							DataRightsAdminActions.rights.listFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	createRight = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.rights.create),
			mergeMap(({ request }) =>
				this.rightsService.create(request).pipe(
					map((right) => DataRightsAdminActions.rights.createSuccess({ right })),
					catchError((error) =>
						of(
							DataRightsAdminActions.rights.createFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	updateRight = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.rights.update),
			mergeMap(({ id, request }) =>
				this.rightsService.update(id, request).pipe(
					map((right) => DataRightsAdminActions.rights.updateSuccess({ right })),
					catchError((error) =>
						of(
							DataRightsAdminActions.rights.updateFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	deleteRight = createEffect(() =>
		this.actions.pipe(
			ofType(DataRightsAdminActions.rights.delete),
			mergeMap(({ id }) =>
				this.rightsService.delete(id).pipe(
					map(() => DataRightsAdminActions.rights.deleteSuccess({ id })),
					catchError((error) =>
						of(
							DataRightsAdminActions.rights.deleteFail({
								error: new FailState(error)
							})
						)
					)
				)
			)
		)
	);

	constructor(
		private actions: Actions,
		private service: DataRightsGroupService,
		private rightsService: DataRightsAdminService
	) {}
}
